// src/api/api.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../env";
import {
    ACCESS_TOKEN_KEY,
    clearStoredSession,
    extractExpiresAtFromAccessToken,
    isIdleTooLong,
    refreshSession,
    writeStoredSession,
} from "./token-storage";

/* -------------------------------------------------------------------------- */
/*                                  AXIOS                                     */
/* -------------------------------------------------------------------------- */

export const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 120_000, // 120s default timeout
});

/* -------------------------------------------------------------------------- */
/*                          SESSION (API + STORAGE)                           */
/* -------------------------------------------------------------------------- */

export function setSession(
    accessToken: string,
    refreshToken: string,
    opts?: {
        expires_in?: number;
        expires_at?: number;
        user_id?: string;
        emit?: boolean; // default true
    },
) {
    // ✅ exactOptionalPropertyTypes-safe: omit undefined optional props
    const payload: {
        access_token: string;
        refresh_token: string;
        expires_in?: number;
        expires_at?: number;
        user_id?: string;
    } = {
        access_token: accessToken,
        refresh_token: refreshToken,
    };

    if (typeof opts?.expires_in === "number") {
        payload.expires_in = opts.expires_in;
    }
    if (typeof opts?.expires_at === "number") {
        payload.expires_at = opts.expires_at;
    }
    if (typeof opts?.user_id === "string" && opts.user_id.trim()) {
        payload.user_id = opts.user_id.trim();
    }

    writeStoredSession(payload);

    api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    if (opts?.emit !== false) {
        window.dispatchEvent(new Event("auth:change"));
    }
}

export function clearSession(opts?: { emit?: boolean }) {
    // Check if there's actually anything to clear — avoids emitting redundant
    // auth:change events that can trigger infinite re-render loops.
    const hadTokens = !!localStorage.getItem("access_token") ||
        !!localStorage.getItem("refresh_token");

    clearStoredSession();
    delete api.defaults.headers.common.Authorization;

    if (opts?.emit !== false && hadTokens) {
        window.dispatchEvent(new Event("auth:change"));
    }
}

/* -------------------------------------------------------------------------- */
/*                               INTERCEPTORS                                 */
/* -------------------------------------------------------------------------- */

api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Attach Authorization unless already present or public endpoint
        const url = String(config.url ?? "");
        const isPublicFp = url.includes("/first-principles/public/");

        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (token && !(config.headers as any)?.Authorization && !isPublicFp) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Forward PostHog session ID so backend errors link to session replays
        try {
            const phSessionId = (window as any).__ph_session_id?.();
            if (phSessionId) {
                config.headers["X-PostHog-Session-Id"] = phSessionId;
            }
        } catch {
            // PostHog not loaded — skip
        }

        const isFormData = typeof FormData !== "undefined" &&
            config.data instanceof FormData;

        if (isFormData) {
            config.headers.delete("Content-Type");
        } else {
            if (!(config.headers as any)["Content-Type"]) {
                (config.headers as any)["Content-Type"] = "application/json";
            }
        }

        return config;
    },
    (error) => Promise.reject(error),
);

api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = (error.config as
            | (InternalAxiosRequestConfig & { _retry?: boolean })
            | undefined) ?? undefined;

        if (!originalRequest) return Promise.reject(error);

        const status = error.response?.status;

        // 402 Payment Required — trigger lockout without clearing auth
        if (status === 402) {
            window.dispatchEvent(new CustomEvent('payment:expired'));
            return Promise.reject(error);
        }

        // Never attempt refresh on refresh endpoint itself (prevents loops)
        const url = String(originalRequest.url ?? "");
        const isRefreshEndpoint = url.includes("/auth/refresh-token");

        if (status === 401 && !originalRequest._retry && !isRefreshEndpoint) {
            originalRequest._retry = true;

            // If user has been idle too long, don't refresh — force logout.
            if (isIdleTooLong()) {
                clearSession({ emit: true });
                return Promise.reject(error);
            }

            const storedRefresh = localStorage.getItem("refresh_token");
            if (!storedRefresh) {
                return Promise.reject(error);
            }

            const refreshed = await refreshSession();

            if (refreshed?.access_token) {
                const expires_at =
                    extractExpiresAtFromAccessToken(refreshed.access_token) ||
                    0;

                // ✅ exactOptionalPropertyTypes-safe: only include expires_in if defined
                setSession(refreshed.access_token, refreshed.refresh_token, {
                    ...(typeof refreshed.expires_in === "number"
                        ? { expires_in: refreshed.expires_in }
                        : {}),
                    expires_at,
                    emit: false,
                });

                // Retry original request with new token
                (originalRequest.headers as any) =
                    (originalRequest.headers as any) || {};
                (originalRequest.headers as any).Authorization =
                    `Bearer ${refreshed.access_token}`;

                const res = await api(originalRequest);
                return res;
            }

            // Refresh returned null. Two possible cases:
            // 1. Invalid refresh token → refreshSession() already called
            //    clearStoredSession(), so hadTokens below is false → no emit.
            // 2. Transient error (network, 5xx) → tokens still in storage.
            //    DON'T clear the session — just fail this request so the caller
            //    can show an error / retry. Nuking the session here would
            //    trigger auth:change → splash screen, losing in-progress work
            //    (e.g. a recording the user just finished).
            const stillHasTokens =
                !!localStorage.getItem(ACCESS_TOKEN_KEY) ||
                !!localStorage.getItem("refresh_token");

            if (stillHasTokens) {
                // Transient failure — session is still valid, just reject
                return Promise.reject(error);
            }

            // Tokens were already invalidated — clean up and redirect
            clearSession({ emit: true });
            return Promise.reject(error);
        }

        return Promise.reject(error);
    },
);
